import { EditType } from "@components/EditableCell"
import { Distribution, DistributionParam, GraphNodeType } from "@custom-types/graphs"
import { ValidationError } from "@features/builder/graph/nodes/validation/types"
import { NodeProperties, NodeType } from "@features/builder/graph/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import PropertiesTable from "@features/graph/details/components/PropertiesTable"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { continuousNodeDistributionProperties } from "utils/graph"

const SELECTABLE_PARENT_NODE_TYPES = [
  NodeType.Deterministic,
  NodeType.Continuous,
  NodeType.Mixture,
  NodeType.Forecast,
  NodeType.Scenario,
]

type ContinuousNodeTableProps = {
  nodeProperties: NodeProperties
  type: NodeType.Continuous | NodeType.Mixture | GraphNodeType.CONTINOUS | GraphNodeType.MIXTURE
  nodeLabelMap: Record<string, string>
  nodeTypeMap: Record<string, NodeType | GraphNodeType>
  onDistributionChange?: (newDistribution: string, rowIndex: number) => void
  enableSelection?: boolean
  validationErrors?: ValidationError[]
}

const ContinuousNodeTable = (props: ContinuousNodeTableProps) => {
  const form = useFormContext<NodeFormValues>()
  const formErrors = form.formState.errors

  const parentOptions = useMemo(
    () =>
      props.nodeProperties.parents_lst
        .filter((parentId) => {
          const parentType = props.nodeTypeMap[parentId] as NodeType
          return SELECTABLE_PARENT_NODE_TYPES.includes(parentType)
        })
        .map((parentId) => ({
          label: props.nodeLabelMap[parentId] ?? parentId,
          value: parentId,
        })),
    [props.nodeProperties.parents_lst, props.nodeLabelMap, props.nodeTypeMap],
  )

  return (
    <PropertiesTable
      type={props.type}
      editable
      labelMap={props.nodeLabelMap}
      nodeTypeMap={props.nodeTypeMap}
      properties={props.nodeProperties}
      control={form.control}
      name="properties"
      enableSelection={props.enableSelection}
      validationErrors={props.validationErrors}
      customizeRow={(row, rowIndex) => {
        const updatedRow = { ...row }

        for (const key of Object.keys(updatedRow)) {
          if (!updatedRow[key]) continue

          updatedRow[key].editOnClick = false

          if (key === "distribution") {
            updatedRow[key].editable = true
            updatedRow[key].type = EditType.SingleOption
            updatedRow[key].options = continuousNodesDistributionsOptions
            updatedRow[key].onChange = (newDistribution) =>
              props.onDistributionChange?.(newDistribution as string, rowIndex)
          }

          const distribution = updatedRow.distribution?.value as Distribution
          const isDistributionParameter =
            continuousNodeDistributionProperties[distribution]?.includes(
              key as DistributionParam,
            ) ?? false

          if (isDistributionParameter) {
            updatedRow[key].type = EditType.NumberOrSingleOption
            updatedRow[key].options = parentOptions
          }

          // overwrite editable if form is disabled
          if (form.formState.disabled) {
            updatedRow[key].editable = false
          }

          const error = formErrors.properties?.[rowIndex]?.[key]
          updatedRow[key].errorMessage = error?.message ?? ""
        }
        return updatedRow
      }}
    />
  )
}

export default ContinuousNodeTable

const continuousNodesDistributionsOptions = Object.keys(continuousNodeDistributionProperties).map(
  (distribution) => ({ label: distribution, value: distribution }),
)
